/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/* eslint-disable react-refresh/only-export-components -- Context pattern requires co-located hook export */

import * as React from "react";
import configuration from "@/config/configuration.js";
import { logger } from "@/lib/logger";

/**
 * WebSocket message types matching Django Channels backend
 */
export interface WebSocketMessage {
  type: string;
  [key: string]: unknown;
}

/**
 * WebSocket client that provides a Socket.IO-like API for Django Channels
 */
export class WebSocketClient {
  private ws: WebSocket | null = null;
  private url: string;
  private eventHandlers: Map<string, Set<(data: unknown) => void>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private pingInterval: NodeJS.Timeout | null = null;
  private isConnecting = false;
  /** Set to true by disconnect() so onclose never triggers a reconnect. */
  private intentionalClose = false;

  constructor(baseUrl: string) {
    // Convert HTTP URL to WebSocket URL
    const wsProtocol = baseUrl.startsWith("https") ? "wss" : "ws";
    const wsHost = baseUrl.replace(/^https?:\/\//, "");
    this.url = `${wsProtocol}://${wsHost}/ws/socket/`;
  }

  /**
   * Connect to WebSocket server
   */
  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      logger.debug("WebSocket already connected or connecting");
      return;
    }

    // S483: Clear the intentional-close flag so a manual reconnect after
    // a disconnect() call works correctly.
    this.intentionalClose = false;
    // S485/CHANGE_12: Reset the retry counter so an intentional reconnect
    // (e.g., after disconnect()) always starts with a full budget. Without this,
    // the counter carried over from the previous connection attempt sequence and
    // a fresh connect() call after hitting the max would bail immediately.
    this.reconnectAttempts = 0;
    this.isConnecting = true;
    logger.info(`WebSocket connecting to: ${this.url}`);

    try {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        logger.info("WebSocket connected");
        this.dispatchEvent("connect", {});
        this.startPing();
      };

      this.ws.onclose = (event) => {
        this.isConnecting = false;
        logger.info(`WebSocket disconnected: ${event.code} ${event.reason}`);
        this.dispatchEvent("disconnect", {
          code: event.code,
          reason: event.reason,
        });
        this.stopPing();
        // S483: Do not reconnect after an intentional close (code 1000 sent by
        // disconnect()). Without this guard, every explicit disconnect triggered
        // reconnection, leaving orphaned backend channels open.
        if (!this.intentionalClose) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        this.isConnecting = false;
        logger.error("WebSocket error:", error);
        this.dispatchEvent("error", error);
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          const eventType = message.type;

          logger.debug(`WebSocket received: ${eventType}`, message);

          // Dispatch to specific event handlers
          if (eventType) {
            this.dispatchEvent(eventType, message);
          }
        } catch (error) {
          logger.error("Failed to parse WebSocket message:", error);
        }
      };
    } catch (error) {
      this.isConnecting = false;
      logger.error("Failed to create WebSocket:", error);
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.stopPing();
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.ws) {
      // S483: Set flag before close() so the onclose callback does not
      // schedule a reconnect for this deliberate teardown.
      this.intentionalClose = true;
      this.ws.close(1000, "Client disconnect");
      this.ws = null;
    }
  }

  /**
   * Send a message to the server
   * @param eventType - The event type (maps to Django Channels 'type' field)
   * @param data - The data to send
   */
  send(eventType: string, data: Record<string, unknown> = {}): void {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      logger.warn("WebSocket not connected, cannot send message");
      return;
    }

    const message: WebSocketMessage = {
      type: eventType,
      ...data,
    };

    logger.debug(`WebSocket sending: ${eventType}`, data);
    this.ws.send(JSON.stringify(message));
  }

  /**
   * Register an event handler
   * @param event - The event name to listen for
   * @param handler - The callback function
   */
  on(event: string, handler: (data: unknown) => void): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)?.add(handler);
  }

  /**
   * Remove an event handler
   * @param event - The event name
   * @param handler - The callback function to remove
   */
  off(event: string, handler: (data: unknown) => void): void {
    this.eventHandlers.get(event)?.delete(handler);
  }

  /**
   * Check if connected
   */
  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Emit an event to the server (alias for send for Socket.IO compatibility)
   * @param eventType - The event type (maps to Django Channels 'type' field)
   * @param data - The data to send
   */
  emit(eventType: string, data: Record<string, unknown> = {}): void {
    this.send(eventType, data);
  }

  /**
   * Dispatch an event to local handlers (internal use)
   */
  private dispatchEvent(event: string, data: unknown): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          logger.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Schedule a reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logger.warn("Max reconnect attempts reached");
      return;
    }

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectAttempts++;

    logger.info(
      `Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`,
    );

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  /**
   * Start ping interval to keep connection alive
   */
  private startPing(): void {
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.send("ping", {});
      }
    }, 30000);
  }

  /**
   * Stop ping interval
   */
  private stopPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }
}

interface WebSocketContextValue {
  socket: WebSocketClient;
  connected: boolean;
}

const WebSocketContext = React.createContext<WebSocketContextValue | null>(
  null,
);

export const WebSocketProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [connected, setConnected] = React.useState(false);

  // Create WebSocket client instance (no side effects here)
  const socket = React.useMemo(() => {
    return new WebSocketClient(configuration.backend_url);
  }, []);

  // Handle connection and cleanup in useEffect (proper place for side effects)
  React.useEffect(() => {
    // Track connection state
    const handleConnect = () => setConnected(true);
    const handleDisconnect = () => setConnected(false);

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);

    // Connect to server
    socket.connect();

    // Cleanup on unmount
    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.disconnect();
    };
  }, [socket]);

  return (
    <WebSocketContext.Provider value={{ socket, connected }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = (): WebSocketContextValue => {
  const context = React.useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};

/**
 * Legacy Socket.IO-compatible hook for backward compatibility
 * This provides the same API as the old useSocket hook
 */
interface LegacySocketContextValue {
  socket: {
    on: (event: string, handler: (data: unknown) => void) => void;
    off: (event: string, handler: (data: unknown) => void) => void;
    emit: (event: string, data: unknown) => void;
    connected: boolean;
  };
}

export const useLegacySocket = (): LegacySocketContextValue => {
  const { socket, connected } = useWebSocket();

  return {
    socket: {
      on: (event: string, handler: (data: unknown) => void) =>
        socket.on(event, handler),
      off: (event: string, handler: (data: unknown) => void) =>
        socket.off(event, handler),
      emit: (event: string, data: unknown) =>
        socket.send(event, data as Record<string, unknown>),
      connected,
    },
  };
};
